<template>
  <!-- Change Orders MANUALES de un subcontrato (conjunto de hitos y finanzas). En el contrato
       principal estas órdenes vienen del control económico (PRM); aquí se dan de alta a mano. -->
  <summary-card :title="cardTitle" :subtitle="subtitle" :mode="mode" :mark="true">
    <template #extra>
      <button class="tbl-add" @click="addRow">
        <b-icon pack="fas" icon="plus" size="is-small"></b-icon>
        <span>{{ $t('prm_summary_sets_co_manual_add') }}</span>
      </button>
    </template>

    <div class="co-scroll">
      <table class="co-table" :class="{ mark: mode }">
        <thead>
          <tr>
            <th>{{ $t('prm_summary_s03_co_order') }}</th>
            <th class="c">{{ $t('prm_summary_s03_co_origin') }}</th>
            <th class="r">{{ $t('prm_summary_sets_co_cost_eur') }}</th>
            <th class="c">{{ $t('prm_summary_s03_co_term') }}</th>
            <th class="c">{{ $t('prm_summary_col_status') }}</th>
            <th class="c">{{ $t('prm_summary_s02_pdf_col') }}</th>
            <th class="c">{{ $t('prm_summary_s03_actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="co in rows">
          <tr :key="co.id">
            <td class="co-in">
              <prm-edit-field :editing="true" :value="co.code" :dense="true" width="120px"
                :placeholder="$t('prm_summary_sets_co_code_ph')" @save="onCo(co.id, 'code', $event)"></prm-edit-field>
            </td>
            <td class="c co-in">
              <prm-edit-field :editing="true" :value="co.origen" type="select" :dense="true" width="90px"
                :options="originOptions" @save="onCo(co.id, 'origen', $event)"></prm-edit-field>
            </td>
            <td class="r co-in">
              <prm-edit-field :editing="true" :value="co.cost" type="number" :dense="true" width="112px"
                :suffix="eurSymbol" :format="fmtEur" @save="onCo(co.id, 'cost', $event)"></prm-edit-field>
            </td>
            <td class="c co-in">
              <prm-edit-field :editing="true" :value="co.plazo" :dense="true" width="96px"
                :placeholder="$t('prm_summary_s03_co_term_ph')" @save="onCo(co.id, 'plazo', $event)"></prm-edit-field>
            </td>
            <td class="c">
              <select class="co-status-sel" :class="co.status" :value="co.status || 'confirmed'"
                      @change="onCo(co.id, 'status', $event.target.value)">
                <option v-for="o in statusOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </td>
            <td class="c">
              <prm-versioned-file :file="fileFor('change_order', co.id)" :editing="true" :project-id="projectId"
                :open="history === co.id" :upload-url="uploadUrl('change_order', co.id)"
                @uploaded="$emit('files-changed')" @toggle-history="toggleHistory(co.id)"></prm-versioned-file>
            </td>
            <td class="c">
              <button class="rm" v-tip="$t('prm_summary_remove_row')" @click="removeRow(co)">
                <b-icon pack="fas" icon="trash-can" size="is-small"></b-icon>
              </button>
            </td>
          </tr>
          <tr v-if="history === co.id && fileFor('change_order', co.id)" :key="co.id + '-h'" class="hist-tr">
            <td colspan="7">
              <prm-file-history :file="fileFor('change_order', co.id)" :base-url="baseUrl" :project-id="projectId"
                @close="history = null"></prm-file-history>
            </td>
          </tr>
          </template>
          <tr v-if="!rows.length"><td class="empty" colspan="7">{{ $t('prm_summary_sets_co_manual_none') }}</td></tr>
        </tbody>
      </table>
    </div>
  </summary-card>
</template>

<script>
  import SummaryCard from './summary-card.vue'
  import PrmEditField from './edit-field.vue'
  import PrmVersionedFile from './versioned-file.vue'
  import PrmFileHistory from './file-history.vue'
  import { fmtEur } from '../shared/format'
  import { tip } from '../shared/tip'

  let seq = 0
  function genId () { return 'co' + Date.now().toString(36) + (seq++).toString(36) + Math.random().toString(36).slice(2, 6) }

  export default {
    name: 'PrmFinanceSetChangeOrders',

    components: { SummaryCard, PrmEditField, PrmVersionedFile, PrmFileHistory },

    directives: { tip },

    props: {
      rows: { type: Array, default: () => [] },
      setName: { type: String, default: '' },
      projectId: { type: [Number, String], required: true },
      baseUrl: { type: String, required: true },
      contractFiles: { type: Object, default: () => ({}) },
      mode: { type: Boolean, default: false },
    },

    data: () => ({ history: null }),

    computed: {
      cardTitle () { return `${this.$t('prm_summary_s03_change_orders')} · ${this.setName}` },
      subtitle () {
        const n = this.rows.length
        const total = this.rows.reduce((s, c) => s + (Number(c.cost) || 0), 0)
        return this.$t('prm_summary_sets_co_manual_sub', { count: n, total: fmtEur(total) })
      },
      originOptions () { return [{ value: 'Cliente', label: 'Cliente' }, { value: 'TKAS', label: 'TKAS' }] },
      statusOptions () {
        return [
          { value: 'confirmed', label: this.$t('prm_summary_s03_co_status_confirmed') },
          { value: 'pending', label: this.$t('prm_summary_s03_co_status_pending') },
        ]
      },
      eurSymbol () { return '€' },
    },

    methods: {
      fmtEur (v) { return fmtEur(v) },
      fileFor (collection, rowId) { return ((this.contractFiles[collection] || {})[rowId]) || null },
      uploadUrl (collection, rowId) { return `${this.baseUrl}/contract-files/${collection}/${rowId}` },
      toggleHistory (id) { this.history = this.history === id ? null : id },

      addRow () {
        this.emit([...this.rows, { id: genId(), code: '', origen: 'Cliente', cost: null, plazo: '', status: 'confirmed' }])
      },

      onCo (id, key, value) {
        const rows = this.rows.map((c) => (c.id === id ? { ...c, [key]: key === 'cost' ? this.parseNum(value) : value } : { ...c }))
        this.emit(rows)
      },

      // Al borrar la orden se borra también su PDF (con todas sus versiones).
      removeRow (co) {
        const file = this.fileFor('change_order', co.id)
        this.emit(this.rows.filter((c) => c.id !== co.id))
        if (file) this.$emit('remove-file', file.id)
      },

      // Normaliza el importe escrito (coma o punto decimal); vacío -> null.
      parseNum (raw) {
        if (raw === '' || raw === null || raw === undefined) return null
        let s = String(raw).trim()
        s = (s.includes(',') && s.includes('.')) ? s.replace(/\./g, '').replace(',', '.') : s.replace(',', '.')
        const n = Number(s)
        return Number.isFinite(n) ? n : null
      },

      emit (rows) { this.$emit('change', rows) },
    },
  }
</script>

<style lang="scss" scoped>
  .tbl-add { display: inline-flex; align-items: center; gap: 5px; border: 1px solid #859bb2; background: #fff; color: #2f5f8f; font-size: 10px; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
  .tbl-add:hover { background: #f4f8fc; }

  .co-scroll { overflow-x: auto; }
  .co-table { width: 100%; border-collapse: collapse; }
  .co-table th { background: #3d6b9a; color: #fff; font-size: 10px; font-weight: 700; padding: 8px 12px; text-align: left; white-space: nowrap; }
  .co-table th.r { text-align: right; } .co-table th.c { text-align: center; }
  .co-table td { border-bottom: 1px solid #eef0f3; padding: 6px 12px; font-size: 11px; color: #1f2837; vertical-align: middle; white-space: nowrap; }
  .co-table td.r { text-align: right; } .co-table td.c { text-align: center; }
  .co-table td.co-in { min-width: 100px; }
  .co-table td.empty { text-align: center; color: #9aa4b2; padding: 10px; white-space: normal; }
  .co-table tbody tr:hover td { background: #f3f6fa; }
  // Modo "fuente de datos": sin fuente real (dato manual) -> tinte ámbar.
  .co-table.mark tbody td { background: #fff8e6; }
  .co-table.mark tbody tr:nth-child(even) td { background: #fdf3d0; }
  .co-table.mark tbody tr:hover td { background: #fbecc0; }
  .co-table .hist-tr td { padding: 6px 10px; background: #fbfcfe; }

  .co-status-sel {
    font-size: 10px; font-weight: 600; padding: 3px 6px; border-radius: 999px;
    border: 1px solid transparent; cursor: pointer; outline: none;
  }
  .co-status-sel.confirmed { background: #eaf6ee; color: #237a43; }
  .co-status-sel.pending { background: #fdf3d7; color: #b7791f; }

  .rm { display: inline-flex; align-items: center; border: 0; background: transparent; color: #9ca3af; cursor: pointer; padding: 2px; }
  .rm:hover { color: #dc2626; }
</style>
